import React from "react";


// ─── inline style tokens (scoped — zero global CSS) ───────────────────────────
const C = {
  green:      "rgb(16, 47, 87)",
  greenDark:  "rgb(11, 33, 61)",
  gold:       "#c8961a",
  white:      "#ffffff",
  bg:         "#f7f7f5",
  border:     "#e0e0da",
  borderLight:"#f0f0ec",
  text:       "#1a1a1a",
  muted:      "#777777",
  subtle:     "#aaaaaa",

  // badge colours
  badgeOpen:   { bg: "#eaf3de", color: "#3B6D11" },
  badgeVideo:  { bg: "#e6f1fb", color: "#185FA5" },
  badgePres:   { bg: "#e6f1fb", color: "#185FA5" },
  badgePolicy: { bg: "#faeeda", color: "#854F0B" },
  badgeDisc:   { bg: "#faeeda", color: "#854F0B" },
  badgeAddr:   { bg: "#eeedfe", color: "#3C3489" },
  badgeClose:  { bg: "#e1f5ee", color: "#0F6E56" },

  // doc icon colours
  docPdf:  { bg: "#feecec", stroke: "#c0392b" },
  docWord: { bg: "#e6f1fb", stroke: "#185FA5" },
  docPpt:  { bg: "#faeeda", stroke: "#854F0B" },
  docImg:  { bg: "#eaf3de", stroke: "#3B6D11" },
};

// ─── SVG icon primitives ──────────────────────────────────────────────────────
const Ico = ({ d, size = 16, color = "currentColor", style: s, ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    width={size} height={size}
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, ...s }}
    aria-hidden="true"
    {...rest}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoCalendar = ({ size, color }) => (
  <svg viewBox="0 0 24 24" width={size || 13} height={size || 13} fill="none"
    stroke={color || C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flexShrink: 0 }}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="16" y1="2" x2="16" y2="6" />
  </svg>
);
const IcoClock = ({ size, color }) => (
  <svg viewBox="0 0 24 24" width={size || 13} height={size || 13} fill="none"
    stroke={color || C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 15" />
  </svg>
);
const IcoPin = ({ size, color }) => (
  <svg viewBox="0 0 24 24" width={size || 13} height={size || 13} fill="none"
    stroke={color || C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const IcoUser = ({ size, color }) => (
  <svg viewBox="0 0 24 24" width={size || 20} height={size || 20} fill="none"
    stroke={color || C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IcoFile = ({ size, stroke }) => (
  <svg viewBox="0 0 24 24" width={size || 18} height={size || 18} fill="none"
    stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="13" y2="17" />
  </svg>
);
const IcoDownload = ({ size, stroke }) => (
  <svg viewBox="0 0 24 24" width={size || 12} height={size || 12} fill="none"
    stroke={stroke || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IcoEye = ({ size }) => (
  <svg viewBox="0 0 24 24" width={size || 12} height={size || 12} fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Government emblem star SVG
const GovEmblem = () => (
  <img
    src="/assets/images/OIP_new-removebg-preview.png"
    alt="Jharkhand Government Logo"
    style={{
      width: 92,
      height: 92,
      objectFit: "contain",
      objectPosition: "center",
      display: "block",
    }}
  />
);

// ─── Reusable badge ───────────────────────────────────────────────────────────
const badgeIconPaths = {
  open:   "M12 2l2 7h7l-5.5 4 2 7L12 16l-5.5 4 2-7L3 9h7z",
  video:  null,
  pres:   null,
  policy: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
  disc:   "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  addr:   "M12 20h9 M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z",
  close:  "M20 6 9 17 4 12",
};

const Badge = ({ type, label, colors }) => {
  const iconPath = badgeIconPaths[type];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 10, fontWeight: 600, letterSpacing: "0.8px",
      textTransform: "uppercase", padding: "2px 8px",
      borderRadius: 4, marginBottom: 6,
      background: colors.bg, color: colors.color,
    }}>
      {iconPath && (
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          strokeLinejoin="round" aria-hidden="true">
          <path d={iconPath} />
        </svg>
      )}
      {label}
    </span>
  );
};

// ─── Section label ────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 10,
    fontSize: 10, fontWeight: 600, letterSpacing: "2px",
    textTransform: "uppercase", color: C.green, marginBottom: 14,
  }}>
    {children}
    <span style={{ flex: 1, height: 1, background: C.border, display: "block" }} />
  </div>
);

// ─── Timeline item ────────────────────────────────────────────────────────────
const TlItem = ({ time, badge, badgeColors, programme, by, gold, last }) => (
  <div style={{ display: "grid", gridTemplateColumns: "120px 20px 1fr", gap: "0 14px", alignItems: "stretch" }}>
    {/* Time */}
    <div style={{ textAlign: "right", padding: "14px 0 20px", display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "flex-start" }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: C.green, lineHeight: 1.35, letterSpacing: "0.2px" }}>
        {time}
      </span>
    </div>
    {/* Spine */}
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{
        width: 10, height: 10, borderRadius: "50%", flexShrink: 0, marginTop: 17,
        background: gold ? C.gold : C.green,
        outline: `2px solid ${C.white}`, outlineOffset: 1.5,
        boxShadow: `0 0 0 1.5px ${gold ? C.gold : C.green}`,
      }} />
      {!last && <div style={{ width: 1, flex: 1, background: C.border, marginTop: 3 }} />}
    </div>
    {/* Content */}
    <div style={{
      padding: "11px 0 20px",
      borderBottom: last ? "none" : `1px solid ${C.borderLight}`,
    }}>
      <Badge type={badge} label={badgeLabelMap[badge]} colors={badgeColors} />
      <div style={{ fontSize: 14, fontWeight: 500, color: C.text, lineHeight: 1.45, marginBottom: by ? 3 : 0 }}>
        {programme}
      </div>
      {by && <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>{by}</div>}
    </div>
  </div>
);

const badgeLabelMap = {
  open: "Opening", video: "Video", pres: "Presentation",
  policy: "Policy", disc: "Discussion", addr: "Address", close: "Closing",
};

// ─── Document card ────────────────────────────────────────────────────────────
const DocCard = ({ name, type, meta, downloadHref, viewHref, iconColors }) => (
  <div style={{
    background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: "14px 16px",
    display: "flex", flexDirection: "column", gap: 8,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 6, flexShrink: 0,
        background: iconColors.bg, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <IcoFile stroke={iconColors.stroke} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: C.text, lineHeight: 1.3, wordBreak: "break-word" }}>{name}</div>
        <div style={{ fontSize: 11, color: C.subtle }}>{meta}</div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
      <a href={downloadHref || "#"} download style={btnPrimary}>
        <IcoDownload size={12} stroke="#fff" /> Download
      </a>
      <a href={viewHref || "#"} target="_blank" rel="noreferrer" style={btnSecondary}>
        <IcoEye size={12} /> View
      </a>
    </div>
  </div>
);

const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 4,
  fontSize: 11, fontWeight: 500, padding: "4px 10px",
  borderRadius: 5, border: `1px solid ${C.green}`,
  background: C.green, color: C.white, cursor: "pointer",
  textDecoration: "none",
};
const btnSecondary = {
  display: "inline-flex", alignItems: "center", gap: 4,
  fontSize: 11, fontWeight: 500, padding: "4px 10px",
  borderRadius: 5, border: `1px solid ${C.border}`,
  background: C.white, color: "#444", cursor: "pointer",
  textDecoration: "none",
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const SCHEDULE = [
  { time: "11:30 – 11:34 AM", badge: "open", colors: C.badgeOpen, programme: "Welcome Address & Lamp Lighting Ceremony", by: "Director Tourism, Jharkhand" },
  { time: "11:34 – 11:36 AM", badge: "video", colors: C.badgeVideo, programme: "Video Presentation 1" },
  { time: "11:36 – 11:41 AM", badge: "pres", colors: C.badgePres, programme: "Context Setting & Presentation on Jharkhand Tourism", by: "Secretary, Tourism, Arts, Culture, Sports & Youth Affairs, Jharkhand" },
  { time: "11:41 – 11:43 AM", badge: "video", colors: C.badgeVideo, programme: "Video Presentation 2" },
  { time: "11:43 AM – 12:03 PM", badge: "policy", colors: C.badgePolicy, programme: "Presentation on Draft Jharkhand Tourism Policy 2026 & Draft Jharkhand Homestay Policy 2026", by: "Secretary, Tourism, Arts, Culture, Sports & Youth Affairs, Jharkhand" },
  { time: "12:03 – 12:43 PM", badge: "disc", colors: C.badgeDisc, gold: true, programme: "Recommendations and Feedback by Distinguished Guests and Dignitaries", by: "Open consultation — 40 minutes" },
  { time: "12:43 – 12:58 PM", badge: "addr", colors: C.badgeAddr, gold: true, programme: "Address by Hon'ble Minister, Tourism, Arts, Culture, Sports & Youth Affairs, Jharkhand" },
  { time: "12:58 – 1:00 PM", badge: "close", colors: C.badgeClose, programme: "Vote of Thanks", by: "Managing Director, Jharkhand Tourism Development Corporation" },
];

const EVENT_DOCUMENT_PDF = "/pdfs/DEPARTMENT OF TOURISM, ART, CULTURE, SPORTS & YOUTH AFFAIR FINAL.pdf";

const DOCUMENTS = [
  { name: "Programme Schedule", type: "PDF", meta: "PDF · 2nd July 2026", iconColors: C.docPdf, downloadHref: EVENT_DOCUMENT_PDF, viewHref: EVENT_DOCUMENT_PDF },
  { name: "Draft Jharkhand Tourism Policy 2026", type: "PDF", meta: "PDF · Policy Document", iconColors: C.docPdf, downloadHref: EVENT_DOCUMENT_PDF, viewHref: EVENT_DOCUMENT_PDF },
  { name: "Draft Jharkhand Homestay Policy 2026", type: "PDF", meta: "PDF · Policy Document", iconColors: C.docPdf, downloadHref: EVENT_DOCUMENT_PDF, viewHref: EVENT_DOCUMENT_PDF },
];

// ─── Responsive hook ──────────────────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < 600 : false
  );
  React.useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function JharkhandEventPage() {
  const isMobile = useIsMobile();

  const px = isMobile ? 18 : 36;
  const timeCol = isMobile ? "90px" : "120px";
  const timeFontSize = isMobile ? 10 : 11;

  return (
    <div style={{
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: 15,
      lineHeight: 1.6,
      color: C.text,
      background: C.bg,
      paddingTop: isMobile ? 72 : 0,
    }}>

      {/* Import Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@300;400;500;600&display=swap');
      `}</style>

      <div style={{ maxWidth: 860, margin: "0 auto", paddingBottom: 60 }}>

        {/* ── HEADER ── */}
        <header style={{ background: C.green, position: "relative" }}>
          <div style={{ height: 5, background: C.gold }} />
          <div style={{ padding: `30px ${px}px 28px` }}>

            {/* Dept row */}
            <div style={{
              display: "flex", alignItems: "center", gap: 14,
              marginBottom: 22, paddingBottom: 18,
              borderBottom: `1px solid rgba(200,150,26,0.35)`,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <div style={{
                width: 92,
                height: 92,
                borderRadius: "50%",
                flexShrink: 0,
                background: C.white,
                border: `2px solid ${C.gold}`,
                boxShadow: "0 0 0 5px rgba(255,255,255,0.12), 0 8px 18px rgba(0,0,0,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}>
                <GovEmblem />
              </div>
              <div>
                <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "1.8px", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 2, display: "block" }}>
                  Government of Jharkhand
                </span>
                <div style={{ fontSize: isMobile ? 14 : 15.5, fontWeight: 700, color: C.white, lineHeight: 1.25 }}>
                  Department of Tourism, Arts, Culture, Sports &  Youth Affairs
                </div>
              </div>
            </div>

            {/* Event title */}
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "2.2px", textTransform: "uppercase", color: C.gold, marginBottom: 7, display: "block" }}>
              Official Programme Schedule
            </span>
            <h1 style={{fontSize: isMobile ? 19 : 24, fontWeight: 700, color: C.white, lineHeight: 1.22, marginBottom: 16 }}>
              Stakeholders Consultation — Jharkhand Tourism Policy 2026
            </h1>

            {/* Meta pills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px" }}>
              {[
                { Icon: IcoCalendar, text: "Thursday, 2nd July 2026" },
                { Icon: IcoClock, text: "11:30 AM – 1:00 PM" },
                { Icon: IcoPin, text: "Jharkhand" },
              ].map(({ Icon, text }) => (
                <span key={text} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 20, padding: "4px 12px",
                  fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.9)",
                }}>
                  <Icon />
                  {text}
                </span>
              ))}
            </div>
          </div>
          <div style={{ height: 4, background: C.gold }} />
        </header>

        {/* ── BODY ── */}
        <main style={{
          background: C.white, padding: `32px ${px}px 36px`,
          border: `1px solid ${C.border}`, borderTop: "none",
        }}>
          {/* Timeline */}
          <SectionLabel>Programme Schedule</SectionLabel>
          <div style={{ marginBottom: 34 }}>
            {SCHEDULE.map((item, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: `${timeCol} 20px 1fr`, gap: `0 ${isMobile ? 10 : 14}px`, alignItems: "stretch" }}>
                {/* Time */}
                <div style={{ textAlign: "right", padding: "14px 0 20px", display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
                  <span style={{ fontSize: timeFontSize, fontWeight: 600, color: C.green, lineHeight: 1.35 }}>
                    {item.time}
                  </span>
                </div>
                {/* Spine */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%", flexShrink: 0, marginTop: 17,
                    background: item.gold ? C.gold : C.green,
                    outline: `2px solid ${C.white}`, outlineOffset: 1.5,
                    boxShadow: `0 0 0 1.5px ${item.gold ? C.gold : C.green}`,
                  }} />
                  {i < SCHEDULE.length - 1 && <div style={{ width: 1, flex: 1, background: C.border, marginTop: 3 }} />}
                </div>
                {/* Content */}
                <div style={{
                  padding: "11px 0 20px",
                  borderBottom: i < SCHEDULE.length - 1 ? `1px solid ${C.borderLight}` : "none",
                }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    fontSize: 10, fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase",
                    padding: "2px 8px", borderRadius: 4, marginBottom: 6,
                    background: item.colors.bg, color: item.colors.color,
                  }}>
                    {badgeLabelMap[item.badge]}
                  </span>
                  <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 500, color: C.text, lineHeight: 1.45, marginBottom: item.by ? 3 : 0 }}>
                    {item.programme}
                  </div>
                  {item.by && <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>{item.by}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Moderator */}
          <div style={{
            background: C.green, borderRadius: 8, padding: "16px 20px",
            display: "flex", alignItems: "center", gap: 14, marginBottom: 34,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
              background: "rgba(255,255,255,0.12)", border: `1px solid rgba(200,150,26,0.5)`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <IcoUser size={20} color={C.gold} />
            </div>
            <div>
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "1.5px", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 3, display: "block" }}>
                Session Moderator
              </span>
              <div style={{ fontSize: 13.5, fontWeight: 500, color: C.white }}>
                Secretary, Tourism, Arts, Culture, Sports &amp; Youth Affairs, Jharkhand
              </div>
            </div>
          </div>

          {/* Documents */}
          <SectionLabel>Event Documents</SectionLabel>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 12,
          }}>
            {DOCUMENTS.map((doc) => (
              <div key={doc.name} style={{
                background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 8, padding: "14px 16px",
                display: "flex", flexDirection: "column", gap: 8,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 6, flexShrink: 0,
                    background: doc.iconColors.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <IcoFile stroke={doc.iconColors.stroke} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.text, lineHeight: 1.3, wordBreak: "break-word" }}>{doc.name}</div>
                    <div style={{ fontSize: 11, color: C.subtle }}>{doc.meta}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                  <a href={doc.downloadHref} download style={btnPrimary}>
                    <IcoDownload size={12} stroke="#fff" /> Download
                  </a>
                  <a href={doc.viewHref} target="_blank" rel="noreferrer" style={btnSecondary}>
                    <IcoEye size={12} /> View
                  </a>
                </div>
              </div>
            ))}
          </div>

        </main>

        {/* ── FOOTER ── */}
        <footer style={{
          background: C.bg, border: `1px solid ${C.border}`, borderTop: "none",
          padding: `16px ${px}px`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 8,
        }}>
          <span style={{ fontSize: 11.5, color: C.subtle }}>
            Stakeholders Consultation · 2nd July 2026 · 11:30 AM – 1:00 PM
          </span>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: C.green }}>
            Jharkhand Tourism Development Corporation
          </span>
        </footer>

      </div>
    </div>
  );
}
